import {categoriesApi} from '../api';
import {useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult} from '@tanstack/react-query';
import {CategoryModel, EditCategoryParams} from '../model';
import {useNotifications} from '../../../shared';

type MoveCategoryParams = {
  categoryId: string;
  newGroupId: string;
};

type UseCategoryByIdResult = {
  getCategory: UseQueryResult<CategoryModel, Error>;
  updateCategoryMutation: UseMutationResult<CategoryModel, Error, EditCategoryParams, unknown>;
  deleteCategoryMutation: UseMutationResult<void, Error, string, unknown>;
  moveCategoryMutation: UseMutationResult<void, Error, MoveCategoryParams, unknown>;
};

export const useCategoryById = (categoryId: string): UseCategoryByIdResult => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  const getCategory = useQuery({
    queryKey: ['categories', categoryId],
    queryFn: () => categoriesApi.getCategoryById(categoryId),
    enabled: Boolean(categoryId),
  });

  const updateCategoryMutation = useMutation({
    mutationFn: (params: EditCategoryParams) => categoriesApi.updateCategory(params),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categories']});
      queryClient.invalidateQueries({queryKey: ['categoryGroups']});
      createNotification({
        title: 'Категория успешно обновлена',
        name: 'updateCategory',
        theme: 'success',
      });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (categoryId: string) => categoriesApi.deleteCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categories']});
      queryClient.invalidateQueries({queryKey: ['categoryGroups']});
      createNotification({
        title: 'Категория успешно удалена',
        name: 'deleteCategory',
        theme: 'success',
      });
    },
  });

  const moveCategoryMutation = useMutation({
    mutationFn: (params: MoveCategoryParams) => categoriesApi.moveCategoryToGroup(params),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categories']});
      queryClient.invalidateQueries({queryKey: ['categoryGroups']});
      createNotification({
        title: 'Категория успешно перемещена',
        name: 'moveCategory',
        theme: 'success',
      });
    },
  });

  return {
    getCategory,
    updateCategoryMutation,
    deleteCategoryMutation,
    moveCategoryMutation,
  };
};
